import type { ReactNode } from 'react';
import { createContext, createElement, useContext, useLayoutEffect } from 'react';

export type WorkspaceLayoutMode = 'default' | 'discovery-focus';

interface WorkspaceLayoutContextValue {
  mode: WorkspaceLayoutMode;
  setMode: (mode: WorkspaceLayoutMode) => void;
  isSidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  showSidebar: () => void;
}

const WorkspaceLayoutContext = createContext<WorkspaceLayoutContextValue | null>(null);

interface WorkspaceLayoutProviderProps {
  value: WorkspaceLayoutContextValue;
  children: ReactNode;
}

export function WorkspaceLayoutProvider({ value, children }: WorkspaceLayoutProviderProps) {
  return createElement(WorkspaceLayoutContext.Provider, { value }, children);
}

export function useWorkspaceLayout() {
  const context = useContext(WorkspaceLayoutContext);
  if (!context) {
    throw new Error('useWorkspaceLayout must be used within a WorkspaceLayoutProvider.');
  }

  return context;
}

export function useDiscoveryFocusLayout(enabled: boolean) {
  const { mode, setMode, isSidebarOpen, setSidebarOpen, showSidebar } = useWorkspaceLayout();

  useLayoutEffect(() => {
    if (!enabled) {
      setMode('default');
      return;
    }

    setMode('discovery-focus');

    return () => {
      setMode('default');
    };
  }, [enabled, setMode]);

  return {
    isFocusMode: enabled && mode === 'discovery-focus',
    isSidebarOpen,
    setSidebarOpen,
    showSidebar,
  };
}
